import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, fromResult, Loaded, Loading, LoadingAdditional, NotFound, ServerError} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {
  changePreviousOrders,
  getPreviousOrders,
  PreviousOrdersActionType,
} from '../../PreviousOrders/ducks/previousOrders';
import {PreviousOrder} from '../../PreviousOrders/models/previousOrders';
import MarketingApi from '../api/MarketingApi';
import {
  addressIsValidating,
  AddressResponse,
  changeFormOrder,
  changeMarketing,
  changeValidateAddress,
  getCostCenters,
  changeCostCenters,
  getMarketing,
  MarketingActionType,
  submitFormOrder,
  validateAddress,
} from '../ducks/Marketing';
import {
  changeMarketingFormStates,
  getMarketingFormStates,
  MarketingFormStateActionType,
} from '../ducks/marketingFormState';
import {FormCostCenterDto, FormOrderDto, Marketing, MarketingFormStateDto} from '../models/Marketing';

type MarketingActions = ReturnType<typeof getMarketing>;

export function* getMarketingFromApi(action: MarketingActions) {
  const {page, filter, sort} = {...action.payload};
  if (page) {
    yield put(changeMarketing(new LoadingAdditional(page)));
  } else {
    yield put(changeMarketing(new Loading(page)));
  }

  const result: AsyncResult<Page<Marketing>> = yield page
    ? call(mergeWithNextPage, {existingPage: page})
    : call(MarketingApi.getMarketingForms, filter, sort, action.payload.isNewYork);

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeMarketing(value)),
      value => put(changeMarketing(value))
    )
  );
}

export function* getMarketingFormCostCenters(action: ReturnType<typeof getCostCenters>) {
  yield put(changeCostCenters(new Loading()));
  try {
    const result: AsyncResult<FormCostCenterDto[]> = yield call(MarketingApi.getFormCostCenters);
    if (E.isRight(result)) {
      yield put(changeCostCenters(new Loaded(result.right)));
    } else {
      yield put(changeCostCenters(new NotFound()));
      yield put(createSnackbar('An error while fetching cost center list. Please try again.'));
    }
  } catch {
    yield put(changeCostCenters(new ServerError()));
  }
}

export function* getMarketingFormStatesApi(action: ReturnType<typeof getMarketingFormStates>) {
  const request = action.payload;
  const result: AsyncResult<MarketingFormStateDto[]> = yield call(MarketingApi.getMarketingFormStates, request);

  if (E.isRight(result)) {
    yield put(changeMarketingFormStates(new Loaded(result.right)));
  } else {
    yield put(createSnackbar('An error ocurred during the View States process. Please try again.'));
  }
}

export function* validateAddressWithApi(action: ReturnType<typeof validateAddress>) {
  yield put(changeValidateAddress(new Loading()));
  yield put(addressIsValidating(true));
  const {address, address2, city, state, zip} = action.payload.viewCart;
  const [...missing]: string[] = action.payload.missingRequiredFields;
  try {
    yield put(changeFormOrder(new Loading()));
    const result: AsyncResult<AddressResponse> = yield call(() =>
      MarketingApi.getValidatedAddress(address!, address2!, city!, state!, zip!).then()
    );

    if (E.isRight(result)) {
      if (result.right.addressValidatedCode === '0002') yield put(changeValidateAddress(new Loaded(result.right)));
      else
        yield put(changeValidateAddress(new NotFound()));
    } else {
      yield put(changeValidateAddress(new NotFound()));
    }
  } catch {
    yield put(changeValidateAddress(new ServerError()));
  } finally {
    yield put(addressIsValidating(false));
  }
}

export function* submitFormOrderApi(action: ReturnType<typeof submitFormOrder>) {
  try {
    yield put(changeFormOrder(new Loading()));

    const result: AsyncResult<FormOrderDto> = yield call(MarketingApi.submitFormOrder, action.payload.viewCart);

    if (E.isRight(result)) {
      yield put(changeFormOrder(new Loaded(result.right)));
    } else {
      yield put(changeFormOrder(new NotFound()));
      yield put(createSnackbar('An error ocurred while Submitting the Order. Please try again.'));
    }
  } catch {
    yield put(changeFormOrder(new ServerError()));
  }
}
type PreviousOrdersActions = ReturnType<typeof getPreviousOrders>;

export function* getPreviousOrdersFromApi(action: PreviousOrdersActions) {
  const {page, filter, sort} = action.payload;
  if (page) {
    yield put(changePreviousOrders(new LoadingAdditional(page)));
  } else {
    yield put(changePreviousOrders(new Loading(page)));
  }

  const result: AsyncResult<Page<PreviousOrder>> = yield page
    ? call(mergeWithNextPage, {existingPage: page})
    : call(MarketingApi.getPreviousOrder, filter, sort, action.payload.isNewYork);

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changePreviousOrders(value)),
      value => put(changePreviousOrders(value))
    )
  );
}

export function* fetchBulletinsSaga() {
  yield takeLatest(MarketingActionType.GET_MARKETING_FORM, getMarketingFromApi);
}

export function* fetchMarketingFormStatesSaga() {
  yield takeLatest(MarketingFormStateActionType.GET_MARKETING_FORM_STATES, getMarketingFormStatesApi);
}

export function* fetchMarketingFormCostCenters() {
  yield takeLatest(MarketingActionType.GET_COST_CENTERS, getMarketingFormCostCenters);
}

export function* fetchAddressValidation() {
  yield takeLatest(MarketingActionType.VALIDATE_ADDRESS, validateAddressWithApi);
}

export function* fetchMarketingFormOrderSaga() {
  yield takeLatest(MarketingActionType.SUBMIT_FORM_ORDER, submitFormOrderApi);
}

export function* fetchPreviousOrdersSaga() {
  yield takeLatest(PreviousOrdersActionType.GET_PREVIOUS_ORDERS, getPreviousOrdersFromApi);
}

export default function*() {
  yield all([
    fetchBulletinsSaga(),
    fetchMarketingFormStatesSaga(),
    fetchAddressValidation(),
    fetchMarketingFormOrderSaga(),
    fetchMarketingFormCostCenters(),
    fetchPreviousOrdersSaga(),
  ]);
}
